import { DotsThreeIcon, GlobeIcon } from '@phosphor-icons/react';
import React, { useEffect, useRef } from 'react';

import { MediaSlot } from './MediaSlot';
import { MissingCreativeBand } from './MissingCreativeBand';
import {
  PlatformLiCommentIcon,
  PlatformLiLikeIcon,
  PlatformLiRepostIcon,
  PlatformLiSendIcon,
} from './PlatformIconsLinkedIn';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  compactNumber,
  platformNaturalMaxWidth,
  resolveAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * LinkedIn's own published single-image-ad spec: intro text truncates past
 * ~150 characters. Source: LinkedIn's Single Image Ads spec page
 * (https://business.linkedin.com/advertise/ads/sponsored-content/single-image-ads-specs,
 * "Introductory text: 150 characters") — unchanged from the pre-existing
 * value in this file, now with a citation.
 */
const LINKEDIN_INTRO_FOLD_LENGTH = 150;

/**
 * LinkedIn's published carousel-card headline limit — 45 characters, vs. 70
 * for a single-image ad's headline. Source: LinkedIn's Carousel Ads spec
 * page (https://www.linkedin.com/help/lms/answer/a427022, "Card headline: 45
 * characters"). Not enforced as a hard truncation here (no fold UI exists
 * for a headline the way `useCaptionFold` folds body copy) — recorded so a
 * future pass adding a live character counter has the real number, not a
 * guess.
 */
const LINKEDIN_CAROUSEL_HEADLINE_LIMIT = 45;
void LINKEDIN_CAROUSEL_HEADLINE_LIMIT; // documented constant, not yet enforced — see comment above

/**
 * LinkedIn's own carousel image spec: square, minimum 1080×1080. Source: same
 * Carousel Ads spec page as above ("1:1 ratio... at least 1080 x 1080
 * pixels"). Distinct from the single-image ad's 1.91:1 landscape default.
 */
const LINKEDIN_CAROUSEL_CARD_RATIO = 1;

/**
 * One card in a LinkedIn CAROUSEL ad. LinkedIn's carousel format (2–10
 * cards, https://www.linkedin.com/help/lms/answer/a427022) gives each card
 * its own image, its own headline, and its own destination URL — but NOT its
 * own description or CTA text; those stay ad-level. `headline` has no
 * fallback to the ad's top-level `headline` prop (unlike Meta's carousel
 * cards) because a LinkedIn carousel's whole point is per-card headlines —
 * defaulting to one shared headline across every card would misrepresent
 * the format.
 */
export interface LinkedInAdCarouselItem {
  imageUrl?: string | null;
  headline?: string;
}

/**
 * LinkedIn SPONSORED CONTENT single-image ad — the paid sibling of
 * `LinkedInPost`, and the LinkedIn counterpart to `MetaAdPreview`/
 * `InstagramAdPreview`.
 *
 * 🔴 Unlike every other replica in this directory, this one is NOT ported
 * from the founder's Figma file — LinkedIn has no frame in it (see
 * `docs/planning/GOAL-AD-PREVIEW-SYSTEM-2026-08-21.md` §1). The second file
 * he offered (`YKdKDU40vYMd4XwhChwFbU`, node `1:1900`, "LinkedIn Regular Post
 * Ad") was inspected and ruled OUT as a literal-port source: it is a
 * LinkedIn Message/Conversation ad (a different ad product than Sponsored
 * Content — back-arrow header, "Sponsorisé" banner, full-width CTA), a full
 * 390×844 phone mockup rather than a standalone card, dark-only, in French,
 * and contains a typo ("LEAM MORE"). The one thing pulled from it that IS
 * genuinely correct and used here: LinkedIn's real post action row is FOUR
 * actions (Like / Comment / Repost / Send), not Facebook's three — already
 * proven right in this codebase by `LinkedInPost.tsx`, the organic sibling,
 * which this component now matches (the pre-existing version of this file
 * had no action row at all).
 *
 * Everything else in this component is sourced from LinkedIn's own published
 * ad specs (cited inline per constant/value) and from `LinkedInPost.tsx`'s
 * already-correct anatomy — NOT invented. See `platform-tokens.ts`'s
 * `linkedin` skin entry for the provenance of every colour value; several are
 * flagged there as best-effort estimates rather than confirmed values,
 * because LinkedIn publishes no public UI color spec the way its ad-format
 * specs are published.
 *
 * The three things that make it a LinkedIn ad rather than a LinkedIn post:
 * "Promoted" replaces the timestamp, the headline sits in its own band
 * beneath the image (LinkedIn puts it below, where Meta puts it in a link
 * block), and the CTA is a fixed-list button rather than free text — which is
 * exactly why the ad-copy generator is told not to write a call to action
 * into the commentary. Passing `cta` is therefore optional and it renders as
 * a chip, not as copy.
 *
 * Renders the platform's OWN LinkedIn skin (`PLATFORM_SKINS.linkedin`), not
 * Mora's semantic tokens — see `platform-tokens.ts`'s file header and
 * `docs/planning/GOAL-AD-PREVIEW-SYSTEM-2026-08-21.md` §2 for the ruling this
 * licenses. Mora's active theme selects WHICH skin variant renders (light ⇒
 * `dark:false`, dark ⇒ `dark:true`); pass `theme` to force a specific variant
 * regardless of Mora's theme (tests, the style guide showing both side by
 * side) — same seam `MetaAdPreview`/`InstagramAdPreview` expose.
 *
 * No separate actor headline/tagline line: real Sponsored Content runs from
 * a LinkedIn Company Page, which has no personal "headline" the way a
 * person's profile does (that's what `LinkedInPost.tsx`'s optional
 * `headline` prop renders, for an organic post from a person). Ad units carry
 * no such field either, so this component doesn't fabricate one — the header
 * is brand name + "Promoted", matching what LinkedIn's own ad chrome shows
 * for a Page-authored ad.
 *
 * Deliberately NOT a `PlatformPreviewKind`, for the same reason
 * `MetaAdPreview`/`InstagramAdPreview` are not: the dispatcher maps a stored
 * `posts.platforms` value to a shell, and an ad unit never carries one.
 *
 * The OUTER card no longer carries a `border` alongside `shadow-elevation-1`
 * — LinkedIn has no Figma source in this system (see `GOAL-AD-PREVIEW-
 * SYSTEM-2026-08-21.md` §1), but `MetaAdPreview`'s and `InstagramAdPreview`'s
 * Figma-verified frames both confirm the platform's own outer card carries
 * no border, only a shadow supplied by the app (`ARTIFACT_PREVIEW_CARD` in
 * `src/lib/ui-tokens.ts` — the same treatment `PostBody`'s preview pane
 * uses), so this file now follows suit for consistency across the family
 * rather than inventing a border-and-shadow combination none of the sourced
 * siblings use. Internal dividers (mini-card border in the carousel,
 * `border-t` between sections) are unaffected.
 */
export interface LinkedInAdPreviewProps {
  brandName: string;
  brandColor?: string;
  avatarUrl?: string;
  /** The intro text above the image. Folds at LinkedIn's own recommended length. */
  commentary: string;
  /** The bold line beneath the image. Ignored during a carousel — each card has its own. */
  headline: string;
  /**
   * Rendered instead of `headline` — muted, normal weight, never bold — when
   * `headline` is empty or whitespace-only. Without this, an empty headline
   * fell back to the literal string "No headline" rendered in the SAME bold
   * style as a real headline, which reads as content rather than an unfilled
   * field (a false statement about the ad — design-system law 3). Pass the
   * field's own "not filled in yet" copy here rather than baking a fake
   * headline into the `headline` prop itself.
   */
  headlinePlaceholder?: string;
  /** The smaller supporting line under the headline. Ignored during a carousel (LinkedIn's carousel format has no per-card or ad-level description slot). */
  description?: string;
  /**
   * The button label. LinkedIn picks this from a fixed list on the creative,
   * so it is not part of the generated copy; when absent the button is simply
   * not drawn rather than filled with an invented default.
   */
  cta?: string | null;
  imageUrl?: string | null;
  aspectRatio?: number | string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /**
   * The filename the missing creative is expected to carry. Same contract as
   * `MetaAdPreviewProps.expectedFilename`: omitted rather than approximated.
   */
  expectedFilename?: string | null;
  /** Rendered inside the empty creative slot — the way out of that state. */
  emptyAction?: React.ReactNode;
  /**
   * Forwarded to `MissingCreativeBand`'s `variant` — 'dashed' (default, "fill
   * me in") or 'quiet' (flat, no dashed border) for a format that is
   * text-only BY DESIGN rather than merely unfilled. See that component's
   * doc comment.
   */
  creativeSlotVariant?: 'dashed' | 'quiet';
  /**
   * Real engagement on a PUBLISHED ad only. Omit entirely for an unpublished
   * draft — the reaction row does not render at all without this, matching
   * every other replica in this directory (`engagementTruth.test.tsx` guards
   * it). When present, renders exactly what's given, including zero.
   */
  engagement?: {
    likes?: number;
    comments?: number;
  };
  /**
   * Carousel cards — LinkedIn's real 2–10 card format
   * (https://www.linkedin.com/help/lms/answer/a427022), reusing the shared
   * `useCarousel` hook (the same hook every organic post and `MetaAdPreview`
   * use) rather than a second carousel implementation. Fewer than 2 items is
   * treated as the single-image layout, using `imageUrl`/`headline`.
   */
  carouselItems?: LinkedInAdCarouselItem[];
  /** Controlled active carousel index (mirrors `useCarousel`'s contract). */
  carouselActiveIndex?: number;
  onCarouselActiveIndexChange?: (index: number) => void;
  /**
   * Forces which LinkedIn skin variant renders, independent of Mora's active
   * theme. Omit to follow Mora's theme (the normal, real-app path); pass
   * explicitly for the style guide's side-by-side light/dark comparison and
   * for tests that must render a deterministic variant.
   */
  theme?: 'light' | 'dark';
  /**
   * Optional node layered over the media region only, above the image — same
   * seam and same contract as `MetaAdPreviewProps.overlay`. Ignored on a
   * carousel — each card is its own creative and there is no single media
   * slot to sit inside (same rule `MetaAdPreview` follows).
   */
  overlay?: React.ReactNode;
  /** Same contract as `MetaAdPreviewProps.embedded`. */
  embedded?: boolean;
  className?: string;
}

export const LinkedInAdPreview: React.FC<LinkedInAdPreviewProps> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  commentary,
  headline,
  headlinePlaceholder,
  description,
  cta,
  imageUrl,
  aspectRatio,
  isLoading = false,
  emptyHint = 'No creative yet — copy only',
  expectedFilename,
  emptyAction,
  creativeSlotVariant,
  engagement,
  carouselItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  theme,
  overlay,
  embedded = false,
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS.linkedin[resolvedTheme];

  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(
    commentary,
    LINKEDIN_INTRO_FOLD_LENGTH,
  );
  const resolvedAspect = resolveAspectRatio(aspectRatio, 1.91);
  const ctaLabel = cta?.trim();
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const hasEngagement = likes != null || comments != null;

  const validCarouselItems = carouselItems && carouselItems.length >= 2 ? carouselItems : undefined;
  const isCarousel = !!validCarouselItems;

  const { safeActive, slides: carouselSlides } = useCarousel(
    validCarouselItems?.map((item) => item.imageUrl ?? ''),
    imageUrl,
    { activeIndex: carouselActiveIndex, onActiveIndexChange: onCarouselActiveIndexChange },
  );

  // Same wiring `MetaAdPreview` uses for its carousel: the shared
  // `useCarousel` hook owns active-index state; this effect just scrolls the
  // corresponding physical mini-card into view when that index changes.
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!isCarousel) return;
    cardRefs.current[safeActive]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [isCarousel, safeActive]);

  const hasMedia = isCarousel || Boolean(imageUrl) || isLoading;

  return (
    <div
      className={cn(embedded ? 'w-full' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${platformNaturalMaxWidth('linkedin-post')}px` }}
    >
      <article
        className="w-full overflow-hidden rounded-lg shadow-elevation-1"
        data-platform="linkedin-ad"
        style={{ backgroundColor: skin.cardBackground }}
      >
        <header className="flex items-start justify-between gap-3 px-3 pb-2 pt-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <PreviewAvatar
              brandName={brandName}
              brandColor={brandColor}
              avatarUrl={avatarUrl}
              className="size-12 rounded-full"
              textClassName="text-caption font-semibold"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
                {brandName}
              </p>
              <div
                className="flex items-center gap-1 text-detail"
                style={{ color: skin.secondaryText }}
              >
                <span>Promoted</span>
                <span className="opacity-40">·</span>
                <GlobeIcon className="size-3" weight="fill" aria-label="Public" />
              </div>
            </div>
          </div>
          <DotsThreeIcon
            className="size-5 shrink-0"
            weight="bold"
            style={{ color: skin.secondaryText }}
          />
        </header>

        {commentary ? (
          <div className="px-3 pb-3">
            <p
              className="whitespace-pre-wrap break-words text-sm leading-relaxed"
              style={{ color: skin.primaryText }}
            >
              {visible}
              {needsFold && !expanded ? (
                <>
                  {'… '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
                    style={{ color: skin.linkText ?? skin.secondaryText }}
                  >
                    see more
                  </button>
                </>
              ) : null}
            </p>
          </div>
        ) : null}

        {isCarousel ? (
          <div
            className="flex w-full snap-x snap-mandatory gap-2 overflow-x-auto px-3 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            role="list"
            aria-label={`${brandName} ad, ${carouselSlides?.length ?? 0} cards`}
          >
            {validCarouselItems.map((item, index) => (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                role="listitem"
                className="flex w-[62%] max-w-[220px] shrink-0 snap-start flex-col overflow-hidden rounded border"
                style={{ borderColor: skin.cardBorder }}
              >
                <MediaSlot
                  ratio={LINKEDIN_CAROUSEL_CARD_RATIO}
                  bg="muted"
                  imageUrl={item.imageUrl}
                  alt={item.headline || `${brandName} ad creative ${index + 1}`}
                />
                <div className="p-2" style={{ backgroundColor: skin.cardBackground }}>
                  <p
                    className="line-clamp-2 text-detail font-semibold leading-snug"
                    style={{ color: skin.primaryText }}
                  >
                    {item.headline || 'No headline'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {hasMedia ? (
              <MediaSlot
                ratio={resolvedAspect}
                bg="muted"
                imageUrl={imageUrl}
                isLoading={isLoading}
                emptyHint={emptyHint}
                alt={headline || `${brandName} ad creative`}
                overlay={overlay}
              />
            ) : (
              <MissingCreativeBand
                hint={emptyHint}
                expectedFilename={expectedFilename}
                action={emptyAction}
                variant={creativeSlotVariant}
              />
            )}

            <div
              className="flex items-center gap-3 border-t px-3 py-2.5"
              style={{ backgroundColor: skin.cardBackground, borderColor: skin.cardBorder }}
            >
              <div className="min-w-0 flex-1">
                {headline?.trim() ? (
                  <p
                    className="line-clamp-2 text-sm font-semibold leading-snug"
                    style={{ color: skin.primaryText }}
                  >
                    {headline}
                  </p>
                ) : (
                  <p
                    className="line-clamp-2 text-sm font-normal leading-snug"
                    style={{ color: skin.secondaryText }}
                  >
                    {headlinePlaceholder ?? 'No headline'}
                  </p>
                )}
                {description?.trim() ? (
                  <p
                    className="mt-0.5 line-clamp-1 text-detail"
                    style={{ color: skin.secondaryText }}
                  >
                    {description.trim()}
                  </p>
                ) : null}
              </div>
              {ctaLabel ? (
                <span
                  className="inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-detail font-semibold"
                  style={{ borderColor: skin.primaryText, color: skin.ctaText }}
                >
                  {ctaLabel}
                </span>
              ) : null}
            </div>
          </>
        )}

        {hasEngagement ? (
          <div
            className="flex items-center justify-between border-t px-3 py-2 text-detail"
            style={{ color: skin.secondaryText, borderColor: skin.cardBorder }}
          >
            {likes != null ? (
              <div className="flex items-center gap-1.5">
                <span className="flex size-4 items-center justify-center rounded-full bg-platform-linkedin">
                  {/* eslint-disable-next-line no-restricted-syntax -- white glyph on the fixed blue badge above, same as LinkedInPost.tsx's identical badge */}
                  <PlatformLiLikeIcon className="size-2.5 text-white" weight="fill" />
                </span>
                <span>{compactNumber(likes)}</span>
              </div>
            ) : (
              <span />
            )}
            {comments != null ? <span>{compactNumber(comments)} comments</span> : null}
          </div>
        ) : null}

        {/* LinkedIn's own action set: Like / Comment / Repost / Send — four
            actions, not Facebook's three. See this component's header
            comment for where that fact came from. */}
        <div className="grid grid-cols-4 px-1 py-0.5" style={{ color: skin.primaryText }}>
          <LinkedInAdAction
            icon={<PlatformLiLikeIcon className="size-5" weight="regular" />}
            label="Like"
          />
          <LinkedInAdAction
            icon={<PlatformLiCommentIcon className="size-5" weight="regular" />}
            label="Comment"
          />
          <LinkedInAdAction
            icon={<PlatformLiRepostIcon className="size-5" weight="regular" />}
            label="Repost"
          />
          <LinkedInAdAction
            icon={<PlatformLiSendIcon className="size-5" weight="regular" />}
            label="Send"
          />
        </div>
      </article>
    </div>
  );
};

const LinkedInAdAction: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <span
    aria-hidden="true"
    className="flex items-center justify-center gap-1.5 rounded-md py-2 text-detail font-medium opacity-80"
  >
    {icon}
    <span>{label}</span>
  </span>
);
